/**
 * Narrow analytics access (dashboard stage E1.5, #1336).
 * Spec: docs/plans/2026-10-01-dashboard-e15-access-grants.md.
 *
 * A grant is a policy layer ON TOP of the org role, not a new role: a subject
 * with an active grant is `managed` and sees only what its capability list
 * names; everyone else stays `legacy` and keeps the role's behaviour.
 *
 * The capability vocabulary is CLOSED. A string outside this enum means
 * nothing — the policy in `@aica/core` (`rbac/access-policy.ts`) drops it, so
 * an unknown capability is a denial, never a widening. Finance, settings and
 * actions are deliberately absent: they stay with the role and are not
 * delegated together with analytics.
 */
import { z } from 'zod/v4';
import { PROJECT_IDS_MAX } from './common';

export const analyticsCapabilitySchema = z.enum([
  // Section aggregates — no employee names
  'aggregates.flow',
  'aggregates.sales',
  'aggregates.quality',
  'aggregates.team',
  'aggregates.voc',
  // Employee scores and ranking
  'team.scores',
  // Record list and card
  'records.read',
  // Transcript, quotes, evidence
  'evidence.text',
  'evidence.audio',
]);

export const ANALYTICS_CAPABILITIES = analyticsCapabilitySchema.options;

export type AnalyticsCapability = z.infer<typeof analyticsCapabilitySchema>;

/** «Аналитик» and «Руководитель группы» — the two presets of the first delivery. */
export const accessPresetSchema = z.enum(['analyst', 'group_lead']);

export type AccessPreset = z.infer<typeof accessPresetSchema>;

/** Bounds of a scope's id list: what an assignment may name, and nothing larger. */
export const ACCESS_SCOPE_MAX_IDS = 200;
export const ACCESS_SCOPE_ID_MAX_LENGTH = 64;

/**
 * A `projects` scope names at most what a project filter may carry
 * (`PROJECT_IDS_MAX`, 40; spec «Решения» item 7). A managed subject who chose
 * nothing is read with the grant's ids AS that filter, so:
 * - a records link, a picker or a cache key built from them must stay a
 *   request the records list and the readers accept (past the limit they
 *   answer 400);
 * - the analytics readers bind each id as a D1 parameter
 *   (`project_id IN (…)`, E1.5 PR 3a), and D1 refuses a statement with more
 *   than 100 bound parameters — the rest of a query binds its own.
 *   `ProjectRepository` binds the list as one `json_each` parameter instead.
 * `departments` is expanded by a subquery and keeps the general bound.
 */
export const ACCESS_PROJECTS_SCOPE_MAX_IDS = PROJECT_IDS_MAX;

/** 1..max ids of 1..64 chars, duplicates collapsed (first occurrence kept). */
const scopeIdsSchema = (max: number) =>
  z
    .array(z.string().min(1).max(ACCESS_SCOPE_ID_MAX_LENGTH))
    .min(1)
    .max(max)
    .transform((ids) => [...new Set(ids)]);

const orgScopeSchema = z.object({ kind: z.literal('org') });
const projectsScopeSchema = z.object({
  kind: z.literal('projects'),
  ids: scopeIdsSchema(ACCESS_PROJECTS_SCOPE_MAX_IDS),
});
const departmentsScopeSchema = z.object({
  kind: z.literal('departments'),
  ids: scopeIdsSchema(ACCESS_SCOPE_MAX_IDS),
});

/**
 * Where a grant applies. `departments` is a subtree of the org structure
 * (members and pending members of these departments and their descendants);
 * an empty id list is not a scope — "nowhere" is the absence of a grant.
 */
export const accessScopeSchema = z.discriminatedUnion('kind', [
  orgScopeSchema,
  projectsScopeSchema,
  departmentsScopeSchema,
]);

export type AccessScope = z.infer<typeof accessScopeSchema>;

/**
 * Which scopes each preset may carry. «Аналитик» sees aggregates of the whole
 * org or of chosen projects; «Руководитель группы» sees everything about a
 * department subtree and nothing else (spec «Решения», item 3). A grant that
 * pairs them otherwise is refused here and read as `unavailable` by
 * `policyFromGrant` in `@aica/core`.
 */
export const PRESET_SCOPE_KINDS = {
  analyst: ['org', 'projects'],
  group_lead: ['departments'],
} as const satisfies Record<AccessPreset, readonly AccessScope['kind'][]>;

/**
 * A capability list as stored: strings outside the current vocabulary (a
 * retired capability) are dropped on parse instead of making the whole grant
 * unreadable. Dropping can only narrow.
 */
const storedCapabilitiesSchema = z
  .array(z.string())
  .transform((caps) =>
    caps.filter(
      (cap): cap is AnalyticsCapability =>
        analyticsCapabilitySchema.safeParse(cap).success
    )
  );

const grantBaseSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  userId: z.string(),
  capabilities: storedCapabilitiesSchema,
  grantedBy: z.string().nullable(),
  grantedAt: z.string(),
  revokedBy: z.string().nullable().optional(),
  revokedAt: z.string().nullable().optional(),
});

/**
 * A stored grant (`access_grants` row) as a DTO. `capabilities` is the
 * snapshot taken when the grant was made; the effective set is that snapshot
 * narrowed by the preset's current list, so a later change to a preset never
 * widens grants already given. User ids are kept as history even after the
 * account is gone (no FK to `users`); `grantedBy` is null only for a grant
 * made without an acting user. The preset decides which scopes are valid.
 */
export const accessGrantSchema = z.discriminatedUnion('preset', [
  grantBaseSchema.extend({
    preset: z.literal('analyst'),
    scope: z.discriminatedUnion('kind', [orgScopeSchema, projectsScopeSchema]),
  }),
  grantBaseSchema.extend({
    preset: z.literal('group_lead'),
    scope: departmentsScopeSchema,
  }),
]);

export type AccessGrant = z.infer<typeof accessGrantSchema>;

// ============================================================================
// Assigning and revoking (E1.5 PR 5)
// ============================================================================

/**
 * The only base roles a grant may ride on (owner decision 05.10, #1336). A
 * `client` or `admin` keeps settings, «Команда», integrations and API keys by
 * role, and an API-key request reads the whole org without the access policy:
 * a narrowed admin could mint an org-wide key. So a grant on them would narrow
 * nothing — `PUT /users/:id/access` refuses them (`grant_role_not_allowed`),
 * and a role change to anything else is refused while a grant is active
 * (`GRANT_BLOCKS_ROLE_ERROR_CODE`, `schemas/users.ts`).
 */
export const ACCESS_GRANT_BASE_ROLES = ['supervisor', 'agent'] as const;

export type AccessGrantBaseRole = (typeof ACCESS_GRANT_BASE_ROLES)[number];

export function isAccessGrantBaseRole(role: string): role is AccessGrantBaseRole {
  return (ACCESS_GRANT_BASE_ROLES as readonly string[]).includes(role);
}

/**
 * `PUT /api/v1/users/:id/access` — the grant to give one member of the
 * selected org. The preset and its scope are a pair (`PRESET_SCOPE_KINDS`):
 * «Аналитик» over the org or projects, «Руководитель группы» over
 * departments; anything else is a 400 here, before any authority is checked.
 * The capability list is not sent: the server snapshots the preset's current
 * list (`PRESET_CAPABILITIES`, `@aica/core`).
 */
export const accessGrantAssignmentSchema = z.discriminatedUnion('preset', [
  z.object({
    preset: z.literal('analyst'),
    scope: z.discriminatedUnion('kind', [orgScopeSchema, projectsScopeSchema]),
  }),
  z.object({
    preset: z.literal('group_lead'),
    scope: departmentsScopeSchema,
  }),
]);

export type AccessGrantAssignment = z.infer<typeof accessGrantAssignmentSchema>;

/**
 * One active grant as the Team screen sees it — ids only, no names: the
 * person's name comes from the team list the screen already holds.
 * `state: 'active'` tells it from {@link unreadableAccessGrantViewSchema}.
 */
export const accessGrantViewSchema = z.object({
  state: z.literal('active'),
  id: z.string(),
  userId: z.string(),
  preset: accessPresetSchema,
  capabilities: z.array(analyticsCapabilitySchema),
  scope: accessScopeSchema,
  grantedBy: z.string().nullable(),
  grantedAt: z.string(),
});

export type AccessGrantView = z.infer<typeof accessGrantViewSchema>;

/**
 * An active grant whose stored row does not parse (an unknown preset, a scope
 * the preset may not carry, malformed JSON). The person it belongs to is LOCKED
 * OUT — the policy reads such a row as `unavailable`, never as the role — and
 * that is a permanent state, not a failed read: asking again changes nothing.
 * It is answered as DATA about the target (200, `state: 'unreadable'`), never
 * as the caller's `access_policy_unavailable` 503 (retryable, `Retry-After`),
 * which says that the CALLER's own policy could not be read.
 *
 * Only what the row says without parsing it: no preset, scope or capabilities
 * (there is nothing trustworthy to show). The screen offers to assign a new
 * grant (`PUT` replaces it) or to revoke it (`DELETE`) — the repair.
 */
export const unreadableAccessGrantViewSchema = z.object({
  state: z.literal('unreadable'),
  id: z.string(),
  userId: z.string(),
  grantedBy: z.string().nullable(),
  grantedAt: z.string(),
});

export type UnreadableAccessGrantView = z.infer<typeof unreadableAccessGrantViewSchema>;

/** A stored active grant as listed or read: a drawable one, or an unreadable one. */
export const accessGrantEntrySchema = z.discriminatedUnion('state', [
  accessGrantViewSchema,
  unreadableAccessGrantViewSchema,
]);

export type AccessGrantEntry = z.infer<typeof accessGrantEntrySchema>;

/**
 * One department of the selected org as the access forms offer it (E1.5 PR
 * 5b): id, name and parent — to choose a «Руководитель группы» scope from and
 * to name one in the «Доступ» column, with its path (a scope on a group lead
 * covers the whole subtree, and two departments may share a name). Read from
 * `GET /api/v1/access/departments`, for a caller who may manage access
 * (`user.manage_access`: client, admin, the platform owner). Reading the org
 * structure itself takes `organization.manage_settings`, which an admin does
 * not hold.
 */
export const accessDepartmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  parentId: z.string().nullable(),
});

export type AccessDepartment = z.infer<typeof accessDepartmentSchema>;

/** `GET /api/v1/access/departments` — the org's departments, in tree order. */
export const accessDepartmentsResponseSchema = z.object({
  departments: z.array(accessDepartmentSchema),
});

export type AccessDepartmentsResponse = z.infer<typeof accessDepartmentsResponseSchema>;

/**
 * `GET /api/v1/access/grants` — the active grants of the selected org's
 * current members, for the «Доступ: …» line of each Team row. A grant that does
 * not parse is listed as `unreadable` — not omitted (the Team screen must show
 * that this person is locked out). Not listed: grants of the platform owner
 * (always `legacy`, a grant on them means nothing) and of someone who is no
 * longer a member of the org.
 */
export const accessGrantsListResponseSchema = z.object({
  grants: z.array(accessGrantEntrySchema),
});

export type AccessGrantsListResponse = z.infer<typeof accessGrantsListResponseSchema>;

/**
 * `GET /api/v1/users/:id/access` — the member's active grant (null: `legacy`,
 * the role decides; `state: 'unreadable'`: a stored grant that does not parse
 * and locks them out — see {@link unreadableAccessGrantViewSchema}) and the
 * departments they head, which the assignment form offers first for
 * «Руководитель группы» (a suggestion, not a rule). The org's departments to
 * choose from are a read of their own (`GET /access/departments`).
 */
export const memberAccessResponseSchema = z.object({
  userId: z.string(),
  grant: accessGrantEntrySchema.nullable(),
  suggestedDepartments: z.array(z.object({ id: z.string(), name: z.string() })),
});

export type MemberAccessResponse = z.infer<typeof memberAccessResponseSchema>;

/**
 * `PUT /api/v1/users/:id/access` — `changed: false` when the same grant was
 * already active. `grant` is the written row read back: `unreadable` only if
 * that row somehow does not parse — the change DID happen (and is audited), so
 * it is answered as a success, not a 500 that invites a retry.
 */
export const assignAccessGrantResponseSchema = z.object({
  userId: z.string(),
  grant: accessGrantEntrySchema,
  changed: z.boolean(),
});

export type AssignAccessGrantResponse = z.infer<typeof assignAccessGrantResponseSchema>;

/** `DELETE /api/v1/users/:id/access` — `revoked: false` when no grant was active. */
export const revokeAccessGrantResponseSchema = z.object({
  userId: z.string(),
  revoked: z.boolean(),
});

export type RevokeAccessGrantResponse = z.infer<typeof revokeAccessGrantResponseSchema>;

/**
 * Why an assignment or a revocation was refused, beyond the plain role 403
 * and the 404 of a person outside the org (body `{ error, code }`):
 *
 * - `self_assignment` (403) — nobody assigns or revokes their own grant;
 * - `target_platform_owner` (422) — the platform owner is always `legacy`,
 *   a grant on them would mean nothing;
 * - `grant_role_not_allowed` (422) — the person's base role is not
 *   `supervisor`/`agent` (`ACCESS_GRANT_BASE_ROLES`);
 * - `scope_not_found` (422) — a project or department is not in THIS org;
 *   one from another org and one that does not exist answer alike (A03);
 * - `grant_exceeds_assigner` (403) — the assigner holds a grant itself. No
 *   path creates such an assigner any more (a grant only rides on
 *   supervisor/agent, who cannot manage access); refused fail-closed;
 * - `grant_conflict` (409) — the person's grant or role, or the assigner's own
 *   authority (still a client/admin of the org), changed between the
 *   read and the write; nothing was written — read again and retry.
 *
 * An unreadable policy of the assigner is the shared 503
 * `access_policy_unavailable` (`accessErrorResponseSchema`).
 */
export const accessGrantErrorCodeSchema = z.enum([
  'self_assignment',
  'target_platform_owner',
  'grant_role_not_allowed',
  'scope_not_found',
  'grant_exceeds_assigner',
  'grant_conflict',
]);

export type AccessGrantErrorCode = z.infer<typeof accessGrantErrorCodeSchema>;

/** The status each code is answered with. */
export const ACCESS_GRANT_ERROR_STATUS = {
  self_assignment: 403,
  target_platform_owner: 422,
  grant_role_not_allowed: 422,
  scope_not_found: 422,
  grant_exceeds_assigner: 403,
  grant_conflict: 409,
} as const satisfies Record<AccessGrantErrorCode, 403 | 409 | 422>;

export const accessGrantErrorResponseSchema = z.object({
  error: z.string(),
  code: accessGrantErrorCodeSchema,
});

export type AccessGrantErrorResponse = z.infer<typeof accessGrantErrorResponseSchema>;

// ============================================================================
// Enforcement contract (E1.5 PR 2)
// ============================================================================

/**
 * Machine-readable codes of the capability gate on the internal API
 * (`/api/v1/*` and its embedded mirror). The body is `{ error, code,
 * retryable, missing? }` — the `{ error, code }` envelope the assistant routes
 * already use — so a client branches on `code`:
 *
 * - `capability_required` (403) — a DECISION: the subject is `managed` and its
 *   grant does not name a capability the route needs. Asking again changes
 *   nothing until the grant does. `missing` lists what was absent.
 * - `access_policy_unavailable` (503) — a FAILURE, not a decision: the policy
 *   could not be read (or the stored grant does not parse), so nobody can say
 *   whether the subject is narrowed. Never answered with the role's data
 *   instead (spec «Решения» item 6, A02). Retryable; carries `Retry-After`.
 *
 * Deliberately NOT in `publicErrorCodeSchema`: the public API is keyed by an
 * API key, which is not a person and stays outside this policy (spec
 * «Применение»), so it can produce neither code.
 */
export const accessErrorCodeSchema = z.enum([
  'capability_required',
  'access_policy_unavailable',
]);

export type AccessErrorCode = z.infer<typeof accessErrorCodeSchema>;

/** The status each code is answered with — the one place the pairing is decided. */
export const ACCESS_ERROR_STATUS = {
  capability_required: 403,
  access_policy_unavailable: 503,
} as const satisfies Record<AccessErrorCode, 403 | 503>;

/** Seconds a client should wait before asking again after `access_policy_unavailable`. */
export const ACCESS_POLICY_RETRY_AFTER_SECONDS = 5;

export const accessErrorResponseSchema = z.object({
  error: z.string(),
  code: accessErrorCodeSchema,
  retryable: z.boolean(),
  /** `capability_required` only: the capabilities the route needs and the grant lacks. */
  missing: z.array(analyticsCapabilitySchema).optional(),
});

export type AccessErrorResponse = z.infer<typeof accessErrorResponseSchema>;

const accessRevisionSchema = z.number().int().nonnegative();

/**
 * The organization's access revision of a response (E1.5 PR 4): set by
 * edge-api on every response whose request read the caller's access policy —
 * every gated read and action and `GET /access/me`. A decimal integer, the
 * `revision` of «my access». The UI fences on it: an answer of an older
 * revision than the one it already knows is not drawn. The same name, sent BY
 * the UI, partitions the browser HTTP cache (`Vary`); the server never reads
 * that request header.
 */
export const ACCESS_REVISION_HEADER = 'X-Access-Revision';

/**
 * `GET /api/v1/access/me` — what the caller may see in the selected
 * organization. The UI builds its sections from this, not from the role.
 *
 * - `legacy` — no grant: the role decides, exactly as before E1.5, and every
 *   capability check passes, so `capabilities` lists all of them;
 * - `managed` — a grant: only `capabilities` count, within `scope`.
 *
 * An unreadable policy is not a third shape: the endpoint answers 503
 * `access_policy_unavailable`, like every other consumer of the policy.
 */
export const accessMeResponseSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('legacy'),
    capabilities: z.array(analyticsCapabilitySchema),
    revision: accessRevisionSchema,
  }),
  z.object({
    mode: z.literal('managed'),
    preset: accessPresetSchema,
    capabilities: z.array(analyticsCapabilitySchema),
    scope: accessScopeSchema,
    revision: accessRevisionSchema,
  }),
]);

export type AccessMeResponse = z.infer<typeof accessMeResponseSchema>;

/** A ceiling, not a limit anyone reaches: a subtree's people, one CRM id per install. */
export const VIEWER_SCOPE_WIRE_PEOPLE_MAX = 20_000;

/** An id of the wire boundary — bounded as a grant's scope bounds it. */
const wireScopeIdSchema = z.string().min(1).max(ACCESS_SCOPE_ID_MAX_LENGTH);

/**
 * The record viewer boundary edge-api computes for an assistant turn
 * (`resolveRecordViewer`) and workflows-api applies IN the query
 * (`buildViewerScopeCondition`). `null` — the whole organization (an org-wide
 * role or an `org` grant).
 *
 * Two shapes, both STRICT:
 *
 * - the role's (`legacy`) — `globalRole` + `hierarchyEnabled`, unchanged;
 * - a managed subject's grant (E1.5 PR 3b.3, #1336) — its projects (no more
 *   than a `projects` scope may name) or the people of its department subtree
 *   (its departments no more than a `departments` scope may name), with no
 *   `globalRole` and no `hierarchyEnabled`. That absence is the deploy-order
 *   guard: edge-api deploys first, and a worker still on the old schema
 *   (which requires both fields) answers a grant boundary 400 — a refused
 *   turn, never a turn read by the role. The worker accepts both, so the old
 *   edge-api's legacy shape keeps working too.
 */
export const viewerScopeWireSchema = z
  .union([
    z
      .object({
        userId: z.string(),
        orgId: z.string(),
        globalRole: z.string().nullable(),
        hierarchyEnabled: z.boolean(),
      })
      .strict(),
    z
      .object({
        userId: z.string().min(1),
        orgId: z.string().min(1),
        grant: z.discriminatedUnion('kind', [
          z
            .object({
              kind: z.literal('projects'),
              projectIds: z.array(wireScopeIdSchema).max(ACCESS_PROJECTS_SCOPE_MAX_IDS),
            })
            .strict(),
          z
            .object({
              kind: z.literal('people'),
              people: z
                .object({
                  orgId: z.string().min(1),
                  ownUserId: z.string().min(1),
                  departmentIds: z.array(wireScopeIdSchema).max(ACCESS_SCOPE_MAX_IDS),
                  resolved: z
                    .object({
                      userIds: z
                        .array(z.string().min(1))
                        .max(VIEWER_SCOPE_WIRE_PEOPLE_MAX),
                      crmIds: z
                        .array(
                          z
                            .object({
                              installationId: z.string().min(1),
                              externalId: z.string().min(1),
                            })
                            .strict()
                        )
                        .max(VIEWER_SCOPE_WIRE_PEOPLE_MAX),
                    })
                    .strict(),
                })
                .strict(),
            })
            .strict(),
        ]),
      })
      .strict(),
  ])
  .nullable();

export type ViewerScopeWire = z.infer<typeof viewerScopeWireSchema>;
